import { ProjectType } from "@/consts/Project";
import { useReadProjectGroupsItemSheetOriginalList } from "@/libs/react-query/hooks/useItemsheetsQuery";
import { useReadProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import { useReadSpecTypeByProjects } from "@/libs/react-query/hooks/useSpecsQuery";
import { SpecType } from "@/types/specs";
import { useMemo } from "react";

// EstimateSelect.tsx 파일에서 산출내역서 공고를 합치기 위한 로직
export function useFilteredOriginals(group_id: string) {
  const { data: originals } =
    useReadProjectGroupsItemSheetOriginalList(group_id);
  const { data: { projects = [] } = {} } = useReadProjectGroups(group_id);
  const specResults = useReadSpecTypeByProjects({ projects });

  const mergedOriginals = useMemo(() => {
    if (!originals) return [];
    const { proj_originals = [], prcr_originals = [] } = originals;

    return [
      ...proj_originals.map((item) => ({
        ...item,
        project_type: ProjectType.PROJ,
      })),
      ...prcr_originals.map((item) => ({
        ...item,
        project_type: ProjectType.PRCR,
      })),
    ];
  }, [originals]);

  const projectSpecMap = useMemo(() => {
    return projects.reduce<Record<string, any>>((acc, project, idx) => {
      const { data: specData } = specResults[idx] || {};
      
      // 조건 수정: 일반공고(PROJ)는 항상 포함, 사전규격/발주계획은 spec_type이 있을 때만 포함
      const shouldInclude = 
        project.project_type === ProjectType.PROJ || 
        (specData?.spec_type && specData.spec_type !== SpecType.NONE);
        
      if (shouldInclude) {
        acc[project.project_id] = {
          ...specData,
          project_type: project.project_type,
          project_title: project.title,
          created_at: project.begin_date,
        };
      }
      return acc;
    }, {});
  }, [projects, specResults]);

  const allOriginals = useMemo(() => {
    const existingIds = new Set(mergedOriginals.map((o) => o.project_id));
    const additional = Object.entries(projectSpecMap)
      .filter(([id]) => !existingIds.has(id))
      .map(([project_id, value]) => ({
        project_id,
        project_type: value.project_type,
        spec_type: value.spec_type,
        project_title: value.project_title,
        created_at: value.created_at,
        is_outdated: false,
        status: null,
      }));

    return [...mergedOriginals, ...additional].sort((a, b) => {
      if (a.is_outdated !== b.is_outdated) return a.is_outdated ? 1 : -1;
      return b.created_at - a.created_at;
    });
  }, [mergedOriginals, projectSpecMap]);

  return {
    allOriginals,
    isLoading: !originals || specResults.some((r) => r.isLoading),
    isError: specResults.some((r) => r.isError),
  };
}
